import {
  type InfiniteData,
  useInfiniteQuery,
  type UseInfiniteQueryOptions
} from "@tanstack/react-query";

import type { GetProductListRequestDto } from "../../dtos/get-product-list-request-dto";
import type { GetProductListResponseDto } from "../../dtos/get-product-list-response-dto";
import { ProductQueryKeys } from "../../generators/product-query-keys";
import { ProductService } from "../../services/product-service";

type UseGetProductListInfiniteQueryParams = {
  queryParams: Omit<GetProductListRequestDto, "page"> & { limit?: number };
  options?: Omit<
    UseInfiniteQueryOptions<
      GetProductListResponseDto,
      Error,
      InfiniteData<GetProductListResponseDto, number>,
      ReturnType<typeof ProductQueryKeys.getProductListInfinite>["queryKey"],
      number
    >,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
  >;
};

export const useGetProductListInfiniteQuery = ({
  queryParams,
  options
}: UseGetProductListInfiniteQueryParams) => {
  return useInfiniteQuery({
    queryKey: ProductQueryKeys.getProductListInfinite({ ...queryParams })
      .queryKey,

    queryFn: async ({ pageParam }) => {
      return await ProductService.getProductList({
        payload: {
          ...queryParams,
          page: pageParam,
          limit: queryParams.limit ?? 10
        }
      });
    },

    getNextPageParam: (lastPage) => {
      const nextPageExist =
        lastPage.metadata.totalPage > lastPage.metadata.page;
      return nextPageExist ? lastPage.metadata.page + 1 : undefined;
    },

    initialPageParam: 1,

    ...options
  });
};
